'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import ProfileContent, { ProfileTab } from './ProfileContent';
import { useTranslation } from '../lib/i18n';
import { useExitAnimation } from '../lib/useExitAnimation';

type ProfileModalProps = {
  open: boolean;
  onClose: () => void;
  initialTab?: ProfileTab;
};

export default function ProfileModal({ open, onClose, initialTab = 'settings' }: ProfileModalProps) {
  const router = useRouter();
  const { t } = useTranslation();
  const exiting = useExitAnimation(open);

  // Esc zavře modal; lock scrollu pozadí, dokud je modal otevřený
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open && !exiting) return null;

  return (
    <div
      className={`fixed inset-0 z-[10001] flex items-center justify-center bg-bark/40 backdrop-blur-sm overflow-y-auto p-2 sm:p-4 ${open ? 'animate-backdropIn' : 'animate-backdropOut'}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={`bg-linen border border-wheat rounded-rustic shadow-xl w-full max-w-5xl my-4 overflow-y-auto max-h-[94vh] relative ${open ? 'animate-modalIn' : 'animate-modalOut'}`}>
        {/* Hlavička modálu */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 sm:px-6 py-3 border-b border-wheat bg-linen/95 backdrop-blur">
          <h2 className="text-lg font-semibold text-bark">{t('profile')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-wood hover:text-bark transition text-2xl leading-none px-2 -mr-2"
            aria-label={t('cancel')}
            title={t('cancel')}
          >
            ×
          </button>
        </div>

        {/* Obsah profilu */}
        <div className="p-4 sm:p-6">
          <ProfileContent
            initialTab={initialTab}
            readHash={false}
            onAuthRedirect={onClose}
            onNavigate={(href) => {
              onClose();
              router.push(href);
            }}
          />
        </div>
      </div>
    </div>
  );
}
